import { HttpClient, HttpContext, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { AppConfigService } from '../config/config.service';
import { IGNORE_ERROR_INTERCEPTOR_TOKEN } from '../../interceptors/error.interceptor';
import { Observable, lastValueFrom } from 'rxjs';
import { Document } from '@common/entities';
import { ToastrService } from 'ngx-toastr';
import { mimeTypeMap } from './mime-type-map';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private FILES_ENDPOINT = this.configService.getConfig().FILES_ENDPOINT;
  private FILES_TARGET = this.configService.getConfig().FILES_TARGET ?? 'file-upload';

  constructor(
    private http: HttpClient,
    private configService: AppConfigService,
    private toasterService: ToastrService
  ) {}

  /**
   * Returns the file extension from a filename without the leading dot
   * @param filename The full filename including extension
   * @returns The extension without the leading dot (e.g., "pdf", "jpg")
   */
  getFileExtension(filename: string): string {
    const extension = filename.slice(((filename.lastIndexOf('.') - 1) >>> 0) + 2);
    return extension;
  }

  /**
   * Maps a file extension to the appropriate mime type
   * @param extension The file extension without leading dot (e.g., "pdf", "jpg")
   * @returns The corresponding MIME type or "application/octet-stream" if unknown
   */
  getMimeType(extension: string): string {
    return mimeTypeMap[extension.toLowerCase()] || 'application/octet-stream';
  }

  /**
   * Opens a new window and displays a document using a blob stream.
   * Determines the appropriate mime type based on the file extension.
   * Includes error handling, loading feedback, and memory management.
   *
   * @param documentKey The filename or key of the document to display
   * @param parentId The ID of the parent entity (application, etc.)
   * @returns Promise that resolves when document is opened or rejects on error
   */
  openBlobInNewWindow(documentKey: string, parentId: string): Promise<void> {
    // Open a new window immediately to avoid popup blockers
    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      this.toasterService.warning('Pop-up blocked by browser. Please allow pop-ups for this site.');
      return Promise.reject(new Error('Pop-up blocked'));
    }

    // Set the document title
    newWindow.document.title = 'Loading document...';

    return new Promise<void>((resolve, reject) => {
      this.getBlobStream(documentKey, parentId).subscribe({
        next: (response: Blob) => {
          try {
            const fileExtension = this.getFileExtension(documentKey);
            const mimeType = this.getMimeType(fileExtension);
            const blobUrl = window.URL.createObjectURL(new Blob([response], { type: mimeType }));

            // Navigate to the blob URL
            newWindow.location.href = blobUrl;

            // Clean up the blob URL when the window is closed
            newWindow.addEventListener('beforeunload', () => {
              window.URL.revokeObjectURL(blobUrl);
            });

            resolve();
          } catch (error) {
            if (newWindow && !newWindow.closed) {
              newWindow.close();
            }
            this.toasterService.error(`Failed to display document: ${error.message || 'Unknown error'}`);
            reject(error);
          }
        },
        error: (error) => {
          if (newWindow && !newWindow.closed) {
            newWindow.close();
          }
          this.toasterService.error(`Failed to retrieve document: ${error.message || 'Unknown error'}`);
          reject(error);
        },
      });
    });
  }

  upload(parentId: string, file: File, ignoreErrorInterceptor = false, validateImageSize?: string) {
    const formData = new FormData();
    formData.append('image', file);
    if (validateImageSize) {
      formData.append('validateImageSize', validateImageSize);
    }
    return this.http
      .post(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/${parentId}`, formData, {
        reportProgress: true,
        observe: 'events',
        context: new HttpContext().set(IGNORE_ERROR_INTERCEPTOR_TOKEN, ignoreErrorInterceptor),
      })
      .pipe<{ type: HttpEventType; loaded?: number; total?: number; key: string }>(
        map((result) => ({
          ...result,
          key: String(result['body']?.filename).substring(String(result['body']?.filename).indexOf('/') + 1),
          azureUrl: String(result['body']?.url),
        }))
      );
  }

  getDocument(key: string, parentId: string) {
    return this.http.get(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/${parentId}/${key}`);
  }

  getBlobStream(key: string, parentId: string) {
    return this.http.get(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/blob/${parentId}/${key}`, {
      responseType: 'blob',
    });
  }

  isFileExists(key: string, parentId: string) {
    return this.http.get(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/exists/${parentId}/${key}`);
  }

  listFiles(parentId: string) {
    return this.http.get(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/list/list-files/${parentId}`);
  }

  deleteFile(key: string, parentId: string) {
    return this.http
      .delete(`${this.FILES_ENDPOINT}/${this.FILES_TARGET}/${parentId}/${key}`, {
        reportProgress: true,
        observe: 'events',
      })
      .toPromise();
  }

  formatBytes(fileSize: number, decimals = 2) {
    if (fileSize === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(fileSize) / Math.log(k));

    return parseFloat((fileSize / Math.pow(k, i)).toFixed(dm)) + sizes[i];
  }

  async validateAzureFiles(documents: Document[], applicationId: string) {
    let azureFiles: string[] = [];

    if (documents) {
      const validDocuments = [];

      for (const document of documents) {
        if (!document.isDeleted) {
          // Only query Azure if a document is added and files array is empty
          if (!azureFiles.length) {
            azureFiles = await lastValueFrom(this.listFiles(applicationId) as Observable<string[]>);
          }
          const documentExists = azureFiles.includes(`${applicationId}/${document.key}`);
          if (documentExists) {
            validDocuments.push(document);
          } else if (document?.isAdded) {
            this.toasterService.error(`Error uploading document, please re-upload: ${document.name}`, 'Upload Error');
          } else {
            this.toasterService.error(
              `Uploaded document no longer exists, please re-upload: ${document.name}`,
              'Upload Error'
            );
          }
          delete document.isAdded;
        } else {
          validDocuments.push(document);
        }
      }

      // Empty the original array
      documents.length = 0;

      // Push valid documents into the original array
      for (const document of validDocuments) {
        documents.push(document);
      }
    }
  }
}
